/**
 * Shared utility that mirrors the backend permission logic for task claiming.
 *
 * Backend (`task-visibility.ts` → `getUserProjectRole`) treats the project owner
 * as having `ADMIN` role, and the `assignToMe` endpoint allows ADMIN or MEMBER
 * roles (but not VIEWER) to claim unassigned/group tasks.
 *
 * This helper centralises the same logic on the frontend so that permission
 * checks remain consistent across components.
 */
import type { Task, Project, User } from '../types';

export function canClaimTask(
  task: Task,
  user: User | null | undefined,
  project: Project | undefined,
): boolean {
  // Must be logged in
  if (!user) return false;

  // Cannot claim if already assigned or completed
  if (task.assigneeId) return false;
  if (task.completed) return false;

  // Determine project ownership / membership
  const isProjectOwner = project?.ownerId === user.id;
  const membership = project?.members?.find((m) => m.userId === user.id);
  const projectRole = membership?.role;

  // Project owner or non-VIEWER member can claim
  const hasClaimPermission =
    isProjectOwner || (!!membership && projectRole !== 'VIEWER');
  if (!hasClaimPermission) return false;

  // Only unassigned tasks or group tasks can be claimed
  return task.isUnassigned || task.isGroupTask;
}
